import React, { useState, useEffect } from 'react';
import {
    ComposedChart,
    Line,
    Bar,
    XAxis,
    YAxis,
    CartesianGrid,
    Tooltip,
    Legend,
    ResponsiveContainer,
    ReferenceLine
} from 'recharts';
import { Download, RefreshCw, ChevronLeft, ArrowUpRight, ArrowDownRight } from 'lucide-react';

interface CashFlowAnalysisProps {
    onMenuSelect: (menuId: string, params?: any) => void;
    tenantName: string;
    currentUser: any;
    apiFetch: (endpoint: string, options?: RequestInit) => Promise<Response>;
    currentTenantId: number | null;
    selectedYear: number;
}

interface MonthlyData {
    month_label: string;
    year: number;
    month: number;
    total_income: number;
    total_expense: number;
    operating_balance: number;
    facility_balance: number;
    financing_balance: number;
    net_cash_flow: number;
    cash_balance_ending: number;
}

const formatCurrency = (amount: number | null | undefined): string => {
    if (amount == null) return "¥0";
    return new Intl.NumberFormat('ja-JP', { style: 'currency', currency: 'JPY', minimumFractionDigits: 0 }).format(amount);
};

const CashFlowAnalysis: React.FC<CashFlowAnalysisProps> = ({
    onMenuSelect,
    tenantName,
    apiFetch,
    currentTenantId,
    selectedYear
}) => {
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState<string | null>(null);
    const [data, setData] = useState<MonthlyData[]>([]);

    const fetchData = async () => {
        if (!currentTenantId) return;
        setLoading(true);
        setError(null);
        try {
            const res = await apiFetch(`/api/analysis/cash-flow/monthly?fiscal_year=${selectedYear}`);
            if (!res.ok) {
                throw new Error('キャッシュフローデータの取得に失敗しました');
            }
            const json = await res.json();
            setData(json.data || []);
        } catch (err: any) {
            setError(err.message || 'データ取得エラー');
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        fetchData();
    }, [currentTenantId, selectedYear]);

    // Summary Metrics Calculation
    const totalIncomeYear = data.reduce((sum, item) => sum + item.total_income, 0);
    const totalExpenseYear = data.reduce((sum, item) => sum + item.total_expense, 0);
    const latestBalance = data.length > 0 ? data[data.length - 1].cash_balance_ending : 0;
    const netCashFlowYear = data.reduce((sum, item) => sum + item.net_cash_flow, 0);

    const CustomTooltip = ({ active, payload, label }: any) => {
        if (active && payload && payload.length) {
            return (
                <div style={{ backgroundColor: '#fff', border: '1px solid #ccc', padding: '10px', borderRadius: '4px', boxShadow: '0 2px 5px rgba(0,0,0,0.1)' }}>
                    <p style={{ margin: '0 0 10px', fontWeight: 'bold' }}>{label}</p>
                    {payload.map((entry: any, index: number) => (
                        <p key={`item-${index}`} style={{ color: entry.color, margin: '4px 0', fontSize: '14px' }}>
                            {entry.name}: {formatCurrency(entry.value)}
                        </p>
                    ))}
                </div>
            );
        }
        return null;
    };

    return (
        <div style={{ padding: '24px', maxWidth: '1400px', margin: '0 auto', fontFamily: 'sans-serif' }}>
            {/* Header */}
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '24px' }}>
                <div>
                    <button
                        onClick={() => onMenuSelect('main-menu')}
                        style={{
                            display: 'flex',
                            alignItems: 'center',
                            backgroundColor: 'transparent',
                            border: 'none',
                            color: '#4B5563',
                            cursor: 'pointer',
                            fontSize: '14px',
                            padding: '0',
                            marginBottom: '8px'
                        }}
                    >
                        <ChevronLeft size={16} style={{ marginRight: '4px' }} />
                        メインメニューへ戻る
                    </button>
                    <h1 style={{ fontSize: '24px', fontWeight: 'bold', color: '#111827', margin: 0 }}>
                        資金収支（キャッシュフロー）推移分析
                    </h1>
                    <p style={{ color: '#6B7280', margin: '4px 0 0', fontSize: '14px' }}>
                        {tenantName} - {selectedYear}年度
                    </p>
                </div>
                <div style={{ display: 'flex', gap: '12px' }}>
                    <button
                        onClick={fetchData}
                        style={{
                            display: 'flex',
                            alignItems: 'center',
                            backgroundColor: '#F3F4F6',
                            color: '#374151',
                            border: '1px solid #D1D5DB',
                            padding: '8px 16px',
                            borderRadius: '6px',
                            cursor: 'pointer',
                            fontWeight: '500'
                        }}
                    >
                        <RefreshCw size={16} style={{ marginRight: '8px' }} />
                        更新
                    </button>
                </div>
            </div>

            {error && (
                <div style={{ backgroundColor: '#FEE2E2', color: '#991B1B', padding: '12px', borderRadius: '6px', marginBottom: '20px' }}>
                    {error}
                </div>
            )}

            {loading ? (
                <div style={{ display: 'flex', justifyContent: 'center', padding: '40px 0' }}>
                    <p style={{ color: '#6B7280' }}>データを読み込み中...</p>
                </div>
            ) : (
                <>
                    {/* Summary Cards */}
                    <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(250px, 1fr))', gap: '20px', marginBottom: '32px' }}>
                        <div style={{ backgroundColor: '#fff', padding: '20px', borderRadius: '12px', border: '1px solid #E5E7EB', boxShadow: '0 1px 3px rgba(0,0,0,0.05)' }}>
                            <div style={{ fontSize: '14px', color: '#6B7280', marginBottom: '8px' }}>当期末支払資金残高（見込）</div>
                            <div style={{ fontSize: '28px', fontWeight: 'bold', color: '#111827' }}>{formatCurrency(latestBalance)}</div>
                        </div>
                        <div style={{ backgroundColor: '#fff', padding: '20px', borderRadius: '12px', border: '1px solid #E5E7EB', boxShadow: '0 1px 3px rgba(0,0,0,0.05)' }}>
                            <div style={{ fontSize: '14px', color: '#6B7280', marginBottom: '8px' }}>年間累積 収入</div>
                            <div style={{ fontSize: '24px', fontWeight: '600', color: '#059669', display: 'flex', alignItems: 'center' }}>
                                <ArrowUpRight size={20} style={{ marginRight: '4px' }} />
                                {formatCurrency(totalIncomeYear)}
                            </div>
                        </div>
                        <div style={{ backgroundColor: '#fff', padding: '20px', borderRadius: '12px', border: '1px solid #E5E7EB', boxShadow: '0 1px 3px rgba(0,0,0,0.05)' }}>
                            <div style={{ fontSize: '14px', color: '#6B7280', marginBottom: '8px' }}>年間累積 支出</div>
                            <div style={{ fontSize: '24px', fontWeight: '600', color: '#DC2626', display: 'flex', alignItems: 'center' }}>
                                <ArrowDownRight size={20} style={{ marginRight: '4px' }} />
                                {formatCurrency(totalExpenseYear)}
                            </div>
                        </div>
                        <div style={{ backgroundColor: '#fff', padding: '20px', borderRadius: '12px', border: '1px solid #E5E7EB', boxShadow: '0 1px 3px rgba(0,0,0,0.05)' }}>
                            <div style={{ fontSize: '14px', color: '#6B7280', marginBottom: '8px' }}>当期資金収支差額</div>
                            <div style={{ fontSize: '24px', fontWeight: '600', color: netCashFlowYear >= 0 ? '#059669' : '#DC2626' }}>
                                {formatCurrency(netCashFlowYear)}
                            </div>
                        </div>
                    </div>

                    {/* Main Chart */}
                    <div style={{ backgroundColor: '#fff', padding: '24px', borderRadius: '12px', border: '1px solid #E5E7EB', boxShadow: '0 1px 3px rgba(0,0,0,0.05)', marginBottom: '32px' }}>
                        <h2 style={{ fontSize: '18px', fontWeight: '600', color: '#111827', margin: '0 0 24px' }}>月次 資金収支・残高推移</h2>
                        <div style={{ height: '400px', width: '100%' }}>
                            <ResponsiveContainer width="100%" height="100%">
                                <ComposedChart data={data} margin={{ top: 20, right: 20, bottom: 20, left: 20 }}>
                                    <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="#E5E7EB" />
                                    <XAxis
                                        dataKey="month_label"
                                        axisLine={false}
                                        tickLine={false}
                                        tick={{ fill: '#6B7280', fontSize: 13 }}
                                        dy={10}
                                    />
                                    <YAxis
                                        yAxisId="left"
                                        axisLine={false}
                                        tickLine={false}
                                        tick={{ fill: '#6B7280', fontSize: 13 }}
                                        tickFormatter={(val) => `¥${(val / 10000).toLocaleString()}万`}
                                    />
                                    <YAxis
                                        yAxisId="right"
                                        orientation="right"
                                        axisLine={false}
                                        tickLine={false}
                                        tick={{ fill: '#6B7280', fontSize: 13 }}
                                        tickFormatter={(val) => `¥${(val / 10000).toLocaleString()}万`}
                                    />
                                    <Tooltip content={<CustomTooltip />} />
                                    <Legend wrapperStyle={{ paddingTop: '20px' }} />
                                    <ReferenceLine y={0} yAxisId="left" stroke="#D1D5DB" />

                                    <Bar yAxisId="left" dataKey="total_income" name="資金収入" fill="#34D399" radius={[4, 4, 0, 0]} maxBarSize={40} />
                                    <Bar yAxisId="left" dataKey="total_expense" name="資金支出" fill="#F87171" radius={[4, 4, 0, 0]} maxBarSize={40} />
                                    <Line
                                        yAxisId="right"
                                        type="monotone"
                                        dataKey="cash_balance_ending"
                                        name="支払資金残高（右軸）"
                                        stroke="#4F46E5"
                                        strokeWidth={3}
                                        dot={{ r: 4, strokeWidth: 2 }}
                                        activeDot={{ r: 6 }}
                                    />
                                </ComposedChart>
                            </ResponsiveContainer>
                        </div>
                    </div>

                    {/* Data Table */}
                    <div style={{ backgroundColor: '#fff', borderRadius: '12px', border: '1px solid #E5E7EB', overflow: 'hidden' }}>
                        <div style={{ padding: '16px 20px', borderBottom: '1px solid #E5E7EB', backgroundColor: '#F9FAFB' }}>
                            <h3 style={{ margin: 0, fontSize: '16px', fontWeight: '600', color: '#374151' }}>月次データ表</h3>
                        </div>
                        <div style={{ overflowX: 'auto' }}>
                            <table style={{ width: '100%', borderCollapse: 'collapse', textAlign: 'right', fontSize: '14px' }}>
                                <thead>
                                    <tr style={{ backgroundColor: '#F3F4F6', color: '#4B5563', borderBottom: '1px solid #E5E7EB' }}>
                                        <th style={{ padding: '12px 16px', textAlign: 'left', fontWeight: '500' }}>月</th>
                                        <th style={{ padding: '12px 16px', fontWeight: '500' }}>資金収入計</th>
                                        <th style={{ padding: '12px 16px', fontWeight: '500' }}>資金支出計</th>
                                        <th style={{ padding: '12px 16px', fontWeight: '500' }}>当期資金収支差額</th>
                                        <th style={{ padding: '12px 16px', fontWeight: '500' }}>支払資金残高</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {data.map((row, idx) => (
                                        <tr key={idx} style={{ borderBottom: '1px solid #E5E7EB', backgroundColor: idx % 2 === 0 ? '#ffffff' : '#F9FAFB' }}>
                                            <td style={{ padding: '12px 16px', textAlign: 'left', fontWeight: '500', color: '#111827' }}>{row.month_label}</td>
                                            <td style={{ padding: '12px 16px', color: '#059669' }}>{formatCurrency(row.total_income)}</td>
                                            <td style={{ padding: '12px 16px', color: '#DC2626' }}>{formatCurrency(row.total_expense)}</td>
                                            <td style={{ padding: '12px 16px', fontWeight: '500', color: row.net_cash_flow >= 0 ? '#059669' : '#DC2626' }}>
                                                {formatCurrency(row.net_cash_flow)}
                                            </td>
                                            <td style={{ padding: '12px 16px', fontWeight: 'bold', color: '#4F46E5' }}>{formatCurrency(row.cash_balance_ending)}</td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        </div>
                    </div>
                </>
            )}
        </div>
    );
};

export default CashFlowAnalysis;
